import * as React from "react";
import { TypedFieldProp } from "react-typed-form";
import styled from "styled-components";
import { Color } from "../../constants/legacy";

type Props = {
  field: TypedFieldProp<string | null>;
  label?: string;
  required?: boolean;
  autoFocus?: boolean;
  /**
   * Useful if on a white background
   */
  bordered?: boolean;
  /**
   * Allow very similar form components to override just the inner element
   */
  innerElement?: React.ReactNode;
};

export default function FieldText({
  field,
  label,
  autoFocus,
  required,
  bordered,
  innerElement,
}: Props) {
  return (
    <div>
      <StyledBox $disabled={field.isLoading}>
        <StyledLabel $empty={!field.value}>{label || field.label}</StyledLabel>
        {innerElement || (
          <StyledInput
            value={field.value || ""}
            onChange={(ev) => field.handleValueChange(ev.target.value)}
            autoFocus={autoFocus}
            disabled={field.isLoading}
            $bordered={bordered || false}
          />
        )}
        {required && <StyledRequired>*</StyledRequired>}
      </StyledBox>
      {field.lastErrorList?.map((error, i) => (
        <StyledError key={i}>{error}</StyledError>
      ))}
    </div>
  );
}

const StyledBox = styled("div")<{ $disabled?: boolean }>`
  background-color: ${(p) => (p.$disabled ? "#ddd" : Color.white)};
  position: relative;
`;

const StyledLabel = styled("label")<{ $empty: boolean }>`
  position: absolute;
  top: 0;
  left: 20px;
  font-size: 11px;
  color: ${Color.grey};
  pointer-events: none;
  transition: all 0.25s;

  ${(p) =>
    p.$empty &&
    `
    top: 15px;
    font-size: 15px;
  `};
`;

const StyledInput = styled("input")<{ $bordered: boolean }>`
  box-sizing: border-box;
  width: 100%;
  background-color: transparent;
  border: 0;
  border-bottom: 1px ${(p) => (p.$bordered ? "#ddd" : "transparent")} solid;
  height: 48px;
  line-height: 48px;
  margin-top: 6px;
  padding: 0 20px;
  transition: box-shadow 0.35s;

  &:focus {
    box-shadow: 0 2px 0 ${Color.orange};
    outline: 0;
  }
`;

const StyledRequired = styled.div`
  color: ${Color.orange};
  position: absolute;
  right: -18px;
  top: 10px;
  font-weight: 700;
  font-size: 24px;
`;

const StyledError = styled.div`
  padding: 8px 20px 0;
  color: red;
  font-size: 12px;
  text-align: left;
`;
